import type { MotionGraphics, VideoDocument } from '@music-vine/setlist';
import type { AdaptiveDownloadPayload } from '@/lib/adaptive-music/assembly/download-version';
import type React from 'react';
import type { Track } from './tracks';

export interface TrackTag {
  display_keyword_sentence_case?: string;
  name: string;
  slug?: string;
  url?: string;
}

export interface Contributor {
  id: number | string;
  name: string;
  slug?: string;
}

interface CurrentEngagement {
  group: number;
  id: number;
  normalised: number;
  plays?: number;
  positive?: number;
  relevance?: number;
}

interface BaseVersion {
  id: number;
  length: number;
  precise_length: number;
  preview_url?: string;
  waveform_json?: string;
}

interface TrendingCustom {
  engagement_value: number;
}

interface DownloadCustom {
  dateCreated: string;
  orfium_code: string;
  play_order: number;
}

export type AssetCustom = TrendingCustom | DownloadCustom;

// Currently there is no differences between music/sfx.
type MusicVersion = BaseVersion;
type SfxVersion = BaseVersion;

type Version = MusicVersion | SfxVersion;

export const ASSET_TYPE = {
  MUSIC: 'music',
  SFX: 'sfx',
  MOTION: 'motion',
  STOCK_VIDEO: 'stock_footage',
} as const;

export type AssetType = (typeof ASSET_TYPE)[keyof typeof ASSET_TYPE];

export interface Asset {
  asset_specific_data: Version;
  asset_type: AssetType;
  contributor: Contributor;
  current_context?: number | string;
  current_engagement: CurrentEngagement;
  custom?: AssetCustom;
  featured_tags?: TrackTag[];
  id: number;
  image: string;
  is_explicit: boolean;
  is_premium: boolean;
  musicvine_track_id?: number;
  name: string;
  slug: string;
  tags?: TrackTag[];
}

export type AssetGroup =
  | {
      type: 'music' | 'sfx';
      data: Track & {
        adaptive?: AdaptiveDownloadPayload;
      };
    }
  | {
      type: 'motion';
      data: {
        id: MotionGraphics['id'];
        name: MotionGraphics['name'];
        slug?: MotionGraphics['slug'];
        image?: MotionGraphics['image'];
        is_premium: MotionGraphics['is_premium'];
        contributor?: {
          name: MotionGraphics['contributor']['name'];
        };
        file_size: MotionGraphics['file_size'];
        compatibility: MotionGraphics['compatibility'];
        compatibilityNode: React.ReactNode;
        software_version?: string;
        is_lut?: boolean;
        is_new?: boolean;
      };
    }
  | {
      type: 'stock_footage';
      data: VideoDocument & {
        name: string;
        is_new?: boolean;
        is_premium?: boolean;
        slug?: string;
      };
    };
